/**
 * docxMedia.ts
 * Helpers for DOCX packages that embed logos / images (word/media/*).
 *
 * Fill/render must keep media parts and relationships intact. Word often
 * collapses adjacent field cells into one paragraph when a logo is inserted —
 * marker parsing handles that separately in docxMarker.ts.
 */

import PizZip from "pizzip";

export interface DocxMediaPart {
  /** Path inside the package, e.g. word/media/image1.png */
  path: string;
  /** File name only. */
  name: string;
  /** Byte size of the media part. */
  size: number;
  /** Relationship ids in document.xml.rels that point at this part. */
  relationshipIds: string[];
}

export interface DocxMediaReport {
  hasMedia: boolean;
  parts: DocxMediaPart[];
  /** True when document.xml references a:blip / v:imagedata drawings. */
  hasDrawings: boolean;
  warnings: string[];
}

function toUint8(buffer: ArrayBuffer | Uint8Array): Uint8Array {
  return buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
}

/**
 * List embedded images and verify each blip relationship target exists.
 */
export function inspectDocxMedia(fileBuffer: ArrayBuffer | Uint8Array): DocxMediaReport {
  const zip = new PizZip(toUint8(fileBuffer));
  const warnings: string[] = [];
  const docXml = zip.files["word/document.xml"]?.asText() ?? "";
  const hasDrawings =
    docXml.includes("w:drawing") ||
    docXml.includes("a:blip") ||
    docXml.includes("v:imagedata") ||
    docXml.includes("w:pict");

  const relsXml = zip.files["word/_rels/document.xml.rels"]?.asText() ?? "";
  const relTargets = new Map<string, string>();
  const relRe =
    /<Relationship\b[^>]*\bId="([^"]+)"[^>]*\bType="[^"]*\/image"[^>]*\bTarget="([^"]+)"[^>]*\/>/gi;
  let m: RegExpExecArray | null;
  while ((m = relRe.exec(relsXml)) !== null) {
    relTargets.set(m[1], m[2].replace(/^\.\.\//, "").replace(/^\//, ""));
  }
  // Also catch Target before Type attribute order variants
  const relRe2 =
    /<Relationship\b[^>]*\bType="[^"]*\/image"[^>]*\bId="([^"]+)"[^>]*\bTarget="([^"]+)"[^>]*\/>/gi;
  while ((m = relRe2.exec(relsXml)) !== null) {
    relTargets.set(m[1], m[2].replace(/^\.\.\//, "").replace(/^\//, ""));
  }

  const parts: DocxMediaPart[] = [];
  for (const path of Object.keys(zip.files)) {
    if (!path.startsWith("word/media/") || zip.files[path].dir) continue;
    const file = zip.files[path];
    const data = file.asUint8Array();
    const relPath = path.replace(/^word\//, "");
    const relationshipIds = [...relTargets.entries()]
      .filter(([, target]) => target === relPath || target.endsWith("/" + relPath.split("/").pop()))
      .map(([id]) => id);
    parts.push({
      path,
      name: path.split("/").pop() ?? path,
      size: data.length,
      relationshipIds,
    });
  }

  // Orphan relationship targets
  for (const [id, target] of relTargets) {
    const full = target.startsWith("word/") ? target : `word/${target}`;
    if (!zip.files[full]) {
      warnings.push(`Logo/ảnh quan hệ ${id} trỏ tới "${target}" nhưng file không có trong package.`);
    }
  }

  if (hasDrawings && parts.length === 0) {
    warnings.push("Template có hình vẽ (drawing) nhưng không thấy file trong word/media/.");
  }

  return {
    hasMedia: parts.length > 0,
    parts,
    hasDrawings,
    warnings,
  };
}

/**
 * Ensure media parts from the source package are still present after a fill.
 * Returns warnings when a logo was dropped.
 */
export function assertMediaPreserved(
  source: ArrayBuffer | Uint8Array,
  filled: ArrayBuffer | Uint8Array
): string[] {
  const before = inspectDocxMedia(source);
  const after = inspectDocxMedia(filled);
  const warnings: string[] = [...after.warnings];
  for (const part of before.parts) {
    if (!after.parts.some((p) => p.path === part.path && p.size === part.size)) {
      warnings.push(`Logo/ảnh "${part.name}" bị mất hoặc đổi kích thước sau khi bind dữ liệu.`);
    }
  }
  return warnings;
}
